// @flow
import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import Experiment from '@homeaway/react-experiment';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {
    HA_SERP_FREE_CANCELLATION_V2,
    HA_SERP_MOBILE_GEO_DISTANCE
} from '../../abacus/experiments';
import HitFreeCancellation from '../HitFreeCancellation/HitFreeCancellation';
import GeoDistance from '../GeoDistance/GeoDistance';
import HitLabel from '../HitLabel/HitLabel';
import TaxesAndFeesPaidAtProperty from './TaxesAndFeesPaidAtProperty';
import BadgeTooltip from './ReviewBadgeTooltip';

import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    exclusivityBadge: ?Badge,
    premierPartnerBadge: ?Badge
} & HitInfoProps;

const Superlative = ({reviewBadges}) => {
    if (!reviewBadges[0].helpText) {
        return (
            <span className="HitInfo__superlative">{reviewBadges[0].name}</span>
        );
    }

    return (
        <span>
            <span className="HitInfo__superlative">{reviewBadges[0].name}</span>
            <BadgeTooltip helpText={reviewBadges[0].helpText} />
        </span>
    );
};

// TODO: move minStay logic to lingua
// https://jira.homeawaycorp.com/browse/SERP-2848
export default function HitInfoMobile({
    brand,
    exclusivityBadge,
    intl,
    isDatelessSearch,
    isMetric,
    listing,
    premierPartnerBadge,
    fullLocale,
    showIncludesTaxesAndFeesMessage
}: Props) {
    const {
        freeCancellationUntil,
        instantBookable,
        minStayRange,
        reviewBadges,
        reviewCount,
        geoDistance
    } = listing;

    // prevents undesired content shift if there is no reviewBadge
    let priceDetailsClass =
        reviewBadges && reviewBadges[0] && reviewBadges[0].helpText
            ? 'HitInfo__priceSuperlativeGroup--extended'
            : 'HitInfo__priceSuperlativeGroup';

    return (
        <Fragment>
            <div className="media-flex__body">
                <div className={priceDetailsClass}>
                    <div className="HitInfo__price">
                        <Price
                            listing={filterGraphQlAnywhere(
                                priceFragments.listing,
                                listing
                            )}
                            useAverageLabel={isDatelessSearch}
                            fullLocale={fullLocale}
                            instantBookable={instantBookable}
                            intl={intl}
                        />
                    </div>
                    {!!reviewBadges &&
                        !!reviewBadges.length && (
                            <Superlative reviewBadges={reviewBadges} />
                        )}
                </div>
                {/* UK-only regulatory requirement: see mapStateToProps in HitInfo.js */}
                {showIncludesTaxesAndFeesMessage && (
                    <div
                        className="HitInfo__taxesAndFeesPaidAtProperty text-muted micro"
                        style={{marginBottom: 8}}
                    >
                        <TaxesAndFeesPaidAtProperty />
                    </div>
                )}

                <div className="HitInfo__details">
                    <Details listing={listing} isMetric={isMetric} />
                </div>
                <Experiment name={HA_SERP_MOBILE_GEO_DISTANCE}>
                    {({On, Off}) => (
                        <React.Fragment>
                            <On>
                                {geoDistance && (
                                    <GeoDistance
                                        geoDistance={geoDistance}
                                        brand={brand}
                                    />
                                )}
                            </On>
                            <Off>
                                {minStayRange &&
                                    minStayRange.minStayLow !== 0 &&
                                    minStayRange.minStayHigh !== 0 &&
                                    isDatelessSearch && (
                                        <div className="HitInfo__minStay">
                                            <FormattedMessage
                                                id="minStayLabel"
                                                description="min stay"
                                                defaultMessage="{MIN_STAY} night min stay"
                                                values={{
                                                    MIN_STAY:
                                                        minStayRange.minStayLow !==
                                                        minStayRange.minStayHigh
                                                            ? `${
                                                                  minStayRange.minStayLow
                                                              } - ${
                                                                  minStayRange.minStayHigh
                                                              }`
                                                            : `${
                                                                  minStayRange.minStayLow
                                                              }`
                                                }}
                                            />
                                        </div>
                                    )}
                            </Off>
                        </React.Fragment>
                    )}
                </Experiment>

                <div className="HitInfo__badgeRatingGroup">
                    {reviewCount > 0 && (
                        <div className="HitInfo__rating">
                            <Rating
                                listing={filterGraphQlAnywhere(
                                    ratingFragments.listing,
                                    listing
                                )}
                            />
                        </div>
                    )}
                    {premierPartnerBadge && (
                        <div
                            className="small"
                            data-wdio="premier-partner-badge"
                            aria-hidden="true"
                        >
                            {premierPartnerBadge.name}
                        </div>
                    )}
                </div>
                {(!!exclusivityBadge || freeCancellationUntil) && (
                    <div className="HitInfo__labels">
                        {!!exclusivityBadge && (
                            <HitLabel
                                brand={brand}
                                id={exclusivityBadge.id}
                                label={exclusivityBadge.name}
                            />
                        )}
                        {freeCancellationUntil && (
                            <Experiment
                                name={HA_SERP_FREE_CANCELLATION_V2}
                                variant={Experiment.ON}
                            >
                                <HitFreeCancellation
                                    formatMessage={intl.formatMessage}
                                />
                            </Experiment>
                        )}
                    </div>
                )}
            </div>
        </Fragment>
    );
}
